import React, { useState } from 'react';
import {
  Zap,
  Settings,
  Shield,
  Layers,
  Code,
  Cpu,
  FlaskConical,
  ArrowLeftRight,
  CheckCircle,
  TrendingUp,
  BarChart3,
  FileJson,
  Target,
  AlertTriangle,
  Server,
  Network,
  Lock,
  LineChart,
  Activity,
  GitBranch,
  Globe,
  ToggleRight,
  ToggleLeft
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

import SpotlightCard from '../ui/SpotlightCard';

const FeatureCard = ({ icon: Icon, title, description, gradient, highlight, delay = 0, isVisible }) => (
  <SpotlightCard
    className={`group p-6 rounded-2xl border transition-all duration-500 hover-lift ${
      highlight
        ? 'glass-intense bg-gradient-to-b from-green-500/10 to-emerald-500/5 border-green-500/30 hover:border-green-500/50'
        : 'glass-card'
    } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    style={{ transitionDelay: `${delay}ms` }}
    spotlightColor={highlight ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 211, 238, 0.12)'}
  >
    {highlight && (
      <div className="absolute -top-3 left-4 z-20">
        <span className="px-2 py-1 text-xs font-bold bg-green-500 text-white rounded-full animate-float-badge">
          KEY FEATURE
        </span>
      </div>
    )}
    <div
      className={`w-12 h-12 rounded-xl ${gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 icon-bounce`}
    >
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
  </SpotlightCard>
);

// Interactive Toggle Switch Component
const ToggleSwitch = ({ enabled, onToggle, disabled = false, label = 'Toggle' }) => (
  <button
    onClick={onToggle}
    disabled={disabled}
    aria-label={`${label} - ${enabled ? 'enabled' : 'disabled'}`}
    aria-pressed={enabled}
    role="switch"
    className={`relative w-11 h-6 rounded-full transition-all duration-300 ${
      disabled
        ? 'bg-cyan-500/50 cursor-not-allowed'
        : enabled
          ? 'bg-green-500 hover:bg-green-400'
          : 'bg-gray-600 hover:bg-gray-500'
    }`}
  >
    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${
      enabled ? 'left-5' : 'left-0.5'
    }`} />
    {disabled && (
      <Lock className="absolute top-1 left-1/2 -translate-x-1/2 w-3 h-3 text-white/70" />
    )}
  </button>
);

const Features = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.3 });
  const [gridRef, gridVisible] = useScrollAnimation({ threshold: 0.1 });
  const [systemRef, systemVisible] = useScrollAnimation({ threshold: 0.2 });
  const [modularRef, modularVisible] = useScrollAnimation({ threshold: 0.2 });
  const [paperRef, paperVisible] = useScrollAnimation({ threshold: 0.2 });

  // Interactive toggle states for demo
  const [moduleStates, setModuleStates] = useState({
    position: true,
    pnl: true,
    risk: true,
    tradeHistory: true,
    metrics: true,
    compliance: true,
    logging: true,
  });

  const toggleModule = (key) => {
    setModuleStates(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Modular architecture data
  const toggleableModules = [
    {
      key: 'position',
      name: 'Position Tracking',
      desc: 'Per-symbol position state',
      icon: LineChart,
      default: true,
      category: 'trading',
      reason: 'Disable if using external position system'
    },
    {
      key: 'pnl',
      name: 'P&L Calculation',
      desc: 'Realized/unrealized tracking',
      icon: TrendingUp,
      default: true,
      category: 'trading',
      reason: 'Disable if using external P&L system'
    },
    {
      key: 'risk',
      name: 'Risk Management',
      desc: '12+ pre-trade checks',
      icon: Shield,
      default: true,
      category: 'trading',
      reason: 'Disable if using external risk system'
    },
    {
      key: 'tradeHistory',
      name: 'Trade History',
      desc: 'Recording & JSON export',
      icon: FileJson,
      default: true,
      category: 'trading',
      reason: 'Disable if logging externally'
    },
    {
      key: 'metrics',
      name: 'Performance Metrics',
      desc: 'Sharpe, Sortino, drawdown',
      icon: BarChart3,
      default: true,
      category: 'monitoring',
      reason: 'Disable if using external analytics'
    },
    {
      key: 'compliance',
      name: 'Regulatory Compliance',
      desc: 'MiFID II, SEC, anti-manipulation',
      icon: Lock,
      default: true,
      category: 'compliance',
      reason: 'Configure per market (EU, US, Crypto, India)',
      special: true
    },
    {
      key: 'logging',
      name: 'Logging Mode',
      desc: 'Internal / External / Off',
      icon: Activity,
      default: true,
      category: 'monitoring',
      reason: '3 modes: internal, Splunk/ELK, or disabled'
    },
  ];

  const features = [
    {
      icon: Zap,
      title: '950M+ Decisions Per Second',
      description:
        'Pipelined throughput benchmark: 954M on ARM64, 450M on x86. Pre-computed indicators with lock-free cache. Continuous hot-path execution.',
      gradient: 'bg-gradient-to-br from-cyan-500 to-blue-500',
    },
    {
      icon: Server,
      title: 'Complete Trading System',
      description:
        'Not just a decision engine. Full OMS, risk manager, position tracker, P&L calculation, and order encoding included.',
      gradient: 'bg-gradient-to-br from-purple-500 to-pink-500',
    },
    {
      icon: FlaskConical,
      title: 'Built-in Paper Trading',
      description:
        'Full simulation mode with 95% fill rate and realistic slippage. Validate strategies before live deployment.',
      gradient: 'bg-gradient-to-br from-green-500 to-emerald-500',
      highlight: true,
    },
    {
      icon: Network,
      title: '6 Native Protocols',
      description:
        'FIX 4.0/4.2/4.4/5.0, SBE, ITCH, OUCH, FAST, WebSocket — all implemented from scratch. Zero external dependencies.',
      gradient: 'bg-gradient-to-br from-indigo-500 to-violet-500',
    },
    {
      icon: Shield,
      title: '12+ Risk Controls',
      description:
        'Position limits, order limits, rate limiting, loss limits, drawdown monitoring, circuit breakers, kill switch built-in.',
      gradient: 'bg-gradient-to-br from-red-500 to-rose-500',
    },
    {
      icon: Lock,
      title: 'MiFID II & SEC Compliant',
      description:
        'RTS 6/25 algorithm controls, SEC 17a-4 WORM logging, anti-manipulation detection (spoofing, layering, wash trading).',
      gradient: 'bg-gradient-to-br from-amber-500 to-yellow-500',
    },
    {
      icon: Settings,
      title: 'No-Code YAML Config',
      description:
        '14 technical data inputs, 6 operators, 3 action types — define your own logic in YAML. You write the strategy, we execute it fast.',
      gradient: 'bg-gradient-to-br from-blue-500 to-cyan-500',
    },
    {
      icon: Activity,
      title: 'Real-Time Monitoring',
      description:
        'Prometheus metrics endpoint, live P&L tracking, position monitoring, order flow visualization, latency histograms.',
      gradient: 'bg-gradient-to-br from-teal-500 to-cyan-500',
    },
    {
      icon: GitBranch,
      title: 'DPDK & RDMA Support',
      description:
        'Optional kernel bypass networking for sub-microsecond wire-to-wire. RDMA for <1µs inter-server communication.',
      gradient: 'bg-gradient-to-br from-orange-500 to-red-500',
    },
  ];

  // Complete System components
  const systemComponents = [
    { name: "Decision Engine", desc: "950M+ decisions/sec", icon: Zap },
    { name: "Hot Path Processor", desc: "Zero-allocation thread", icon: Cpu },
    { name: "Risk Manager", desc: "12+ pre-trade checks", icon: Shield },
    { name: "Order Manager", desc: "FIX-compliant OMS", icon: FileJson },
    { name: "Position Manager", desc: "Real-time P&L tracking", icon: LineChart },
    { name: "Feed Handlers", desc: "ITCH, FAST, WebSocket", icon: Network },
    { name: "Order Encoders", desc: "OUCH, FIX, SBE codecs", icon: Code },
    { name: "Paper Trader", desc: "Full simulation mode", icon: FlaskConical },
    { name: "Compliance", desc: "MiFID II, SEC built-in", icon: Lock },
    { name: "Audit Trail", desc: "SHA-256 tamper-proof", icon: Shield },
    { name: "Prometheus", desc: "Production monitoring", icon: Activity },
    { name: "PTP Time Sync", desc: "100µs clock accuracy", icon: Target },
  ];

  // Paper Trading detailed feature breakdown
  const paperTradingFeatures = [
    {
      icon: Target,
      title: '95% Fill Rate',
      description: 'Realistic order simulation, not fake 100% fills',
    },
    {
      icon: TrendingUp,
      title: '2 BPS Slippage',
      description: 'Simulated slippage matches real market conditions',
    },
    {
      icon: BarChart3,
      title: 'Full P&L Tracking',
      description: 'Real-time position tracking and performance stats',
    },
    {
      icon: FileJson,
      title: 'Trade Export',
      description: 'JSON export for external analysis and reporting',
    },
  ];

  return (
    <section id="features" className="py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-6">
            <Server className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-yellow-400">Complete Trading Infrastructure</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            A Complete <span className="text-gradient">HFT System</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Not just a decision engine — a complete institutional-grade HFT infrastructure.
            12 integrated components, 6 native protocols, and 950M+ decisions/sec throughput.
          </p>
        </div>

        {/* Features Grid */}
        <div ref={gridRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} delay={index * 100} isVisible={gridVisible} />
          ))}
        </div>

        {/* Complete System Breakdown */}
        <div
          ref={systemRef}
          className={`max-w-6xl mx-auto mb-16 transition-all duration-1000 ${
            systemVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="p-8 rounded-2xl glass-intense border border-white/10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 mb-4">
                <Layers className="w-5 h-5 text-purple-400" />
                <span className="text-sm font-bold text-purple-400">COMPLETE SYSTEM</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                12 Integrated Components — Not Just a Decision Engine
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                ASTRA provides 90% of the trading infrastructure you need.
                You provide exchange connectivity and order book maintenance.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {systemComponents.map((comp, index) => (
                <div
                  key={index}
                  className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-cyan-500/30 transition-colors group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <comp.icon className="w-5 h-5 text-cyan-400 group-hover:scale-110 transition-transform" />
                    <span className="font-medium text-white text-sm">{comp.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{comp.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <p className="text-sm">
                  <span className="text-green-400 font-medium">What ASTRA provides:</span>
                  <span className="text-muted-foreground"> Decision engine, OMS, risk manager, feed handlers, order encoders, compliance, monitoring — everything except exchange access.</span>
                </p>
              </div>
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-sm">
                  <span className="text-yellow-400 font-medium">What you provide:</span>
                  <span className="text-muted-foreground"> Exchange membership, order book maintenance, co-location infrastructure, and network connectivity.</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modular Architecture - Toggleable Features */}
        <div
          ref={modularRef}
          className={`max-w-5xl mx-auto mb-16 transition-all duration-1000 ${
            modularVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="p-8 rounded-2xl glass-intense border border-white/10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/30 mb-4">
                <Settings className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-bold text-blue-400">MODULAR ARCHITECTURE</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                7 Toggleable Modules — Configure for Your Market
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Every module can be enabled or disabled via YAML. Trading in crypto? Skip MiFID II.
                Have your own P&L system? Disable ours. Only pay for compute you use.
              </p>
            </div>

            {/* Interactive Demo */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
              <div className="flex items-center gap-2 mb-3">
                <ToggleRight className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-white">Interactive Demo — Try toggling modules:</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Click any toggle below to see how modules can be configured. In production, you control this via <code className="px-1.5 py-0.5 rounded bg-black/30 text-cyan-400">astra_config.yaml</code>
              </p>
            </div>

            {/* Toggleable Modules Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {toggleableModules.map((module) => {
                const isEnabled = moduleStates[module.key];
                const ModuleIcon = module.icon;
                return (
                  <div
                    key={module.key}
                    className={`p-4 rounded-xl border transition-all duration-300 ${
                      isEnabled
                        ? 'bg-white/5 border-green-500/30 hover:border-green-500/50'
                        : 'bg-white/[0.02] border-white/10 opacity-60'
                    } ${module.special ? 'ring-1 ring-yellow-500/30' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          isEnabled ? 'bg-green-500/20' : 'bg-white/5'
                        }`}>
                          <ModuleIcon className={`w-4 h-4 ${isEnabled ? 'text-green-400' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white">{module.name}</div>
                          <div className="text-xs text-muted-foreground">{module.desc}</div>
                        </div>
                      </div>
                      <ToggleSwitch
                        enabled={isEnabled}
                        onToggle={() => toggleModule(module.key)}
                        label={module.name}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                      <span className={`text-xs ${isEnabled ? 'text-green-400' : 'text-gray-500'}`}>
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Default: <span className="text-cyan-400">{module.default ? 'On' : 'Off'}</span>
                      </span>
                    </div>
                    {module.special && (
                      <div className="mt-2 px-2 py-1 rounded bg-yellow-500/10 border border-yellow-500/20">
                        <p className="text-xs text-yellow-400">
                          <Globe className="w-3 h-3 inline mr-1" />
                          {module.reason}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Core Engine - Always On */}
            <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20 mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white">Decision Engine Core</span>
                    <span className="px-2 py-0.5 text-xs font-bold bg-cyan-500 text-white rounded">ALWAYS ON</span>
                  </div>
                  <p className="text-xs text-muted-foreground">950M+ decisions/sec ASTRA Hot Path Engine™ — the heart of the system</p>
                </div>
                <ToggleSwitch enabled={true} disabled={true} label="Decision Engine Core" />
              </div>
            </div>

            {/* Why This Matters */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                <Globe className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <div className="font-medium text-white text-sm mb-1">Global Markets</div>
                <p className="text-xs text-muted-foreground">
                  MiFID II for EU, SEC for US, SEBI for India, minimal for Crypto — configure per market
                </p>
              </div>
              <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                <Layers className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <div className="font-medium text-white text-sm mb-1">Existing Systems</div>
                <p className="text-xs text-muted-foreground">
                  Already have P&L or risk? Disable ours. ASTRA integrates with your infrastructure
                </p>
              </div>
              <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                <Cpu className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <div className="font-medium text-white text-sm mb-1">Resource Efficiency</div>
                <p className="text-xs text-muted-foreground">
                  Disabled modules = zero CPU cycles. Only pay for compute you actually use
                </p>
              </div>
            </div>

            {/* YAML Example */}
            <div className="mt-6 p-4 rounded-xl bg-black/30 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Code className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-medium text-white">Configuration Example</span>
              </div>
              <pre className="font-mono text-xs overflow-x-auto">
                <code>
                  <span className="text-purple-400">modules:</span>{'\n'}
                  <span className="text-muted-foreground">  </span><span className="text-blue-400">position:</span>{'\n'}
                  <span className="text-muted-foreground">    </span><span className="text-blue-400">enabled:</span> <span className={moduleStates.position ? 'text-green-400' : 'text-red-400'}>{moduleStates.position ? 'true' : 'false'}</span>{'\n'}
                  <span className="text-muted-foreground">  </span><span className="text-blue-400">pnl:</span>{'\n'}
                  <span className="text-muted-foreground">    </span><span className="text-blue-400">enabled:</span> <span className={moduleStates.pnl ? 'text-green-400' : 'text-red-400'}>{moduleStates.pnl ? 'true' : 'false'}</span>{'\n'}
                  <span className="text-muted-foreground">  </span><span className="text-blue-400">risk:</span>{'\n'}
                  <span className="text-muted-foreground">    </span><span className="text-blue-400">enabled:</span> <span className={moduleStates.risk ? 'text-green-400' : 'text-red-400'}>{moduleStates.risk ? 'true' : 'false'}</span>{'\n'}
                  <span className="text-muted-foreground">  </span><span className="text-blue-400">tradeHistory:</span>{'\n'}
                  <span className="text-muted-foreground">    </span><span className="text-blue-400">enabled:</span> <span className={moduleStates.tradeHistory ? 'text-green-400' : 'text-red-400'}>{moduleStates.tradeHistory ? 'true' : 'false'}</span>{'\n'}
                  <span className="text-muted-foreground">  </span><span className="text-blue-400">metrics:</span>{'\n'}
                  <span className="text-muted-foreground">    </span><span className="text-blue-400">enabled:</span> <span className={moduleStates.metrics ? 'text-green-400' : 'text-red-400'}>{moduleStates.metrics ? 'true' : 'false'}</span>{'\n'}
                  <span className="text-muted-foreground">  </span><span className="text-blue-400">compliance:</span>{'\n'}
                  <span className="text-muted-foreground">    </span><span className="text-blue-400">enabled:</span> <span className={moduleStates.compliance ? 'text-green-400' : 'text-red-400'}>{moduleStates.compliance ? 'true' : 'false'}</span>{'\n'}
                  <span className="text-muted-foreground">    </span><span className="text-blue-400">region:</span> <span className="text-yellow-400">"EU"</span>  <span className="text-gray-500"># EU, US, IN, CRYPTO</span>{'\n'}
                  <span className="text-muted-foreground">  </span><span className="text-blue-400">logging:</span>{'\n'}
                  <span className="text-muted-foreground">    </span><span className="text-blue-400">enabled:</span> <span className={moduleStates.logging ? 'text-green-400' : 'text-red-400'}>{moduleStates.logging ? 'true' : 'false'}</span>{'\n'}
                  <span className="text-muted-foreground">    </span><span className="text-blue-400">mode:</span> <span className="text-yellow-400">"external"</span>  <span className="text-gray-500"># internal, external, off</span>
                </code>
              </pre>
            </div>
          </div>
        </div>

        {/* Paper Trading Deep Dive - KEY DIFFERENTIATOR */}
        <div
          ref={paperRef}
          className={`max-w-5xl mx-auto transition-all duration-1000 ${
            paperVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="p-8 rounded-2xl bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-teal-500/10 border border-green-500/20">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 mb-4">
                <FlaskConical className="w-5 h-5 text-green-400" />
                <span className="text-sm font-bold text-green-400">INTEGRATED SIMULATION</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Production-Grade Paper Trading
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Every deployment includes built-in paper trading with realistic market simulation.
                Same ~2ns amortized decision engine, same protocols, same infrastructure — with zero capital risk.
                One configuration toggle to switch between simulation and live execution.
              </p>
            </div>

            {/* Paper vs Live comparison */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="p-5 rounded-xl bg-background/50 border border-border">
                <h4 className="font-bold text-white mb-4 flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-green-400" />
                  Paper Trading Mode
                </h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Same 950M+/sec decision engine
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Realistic 95% fill rate simulation
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    2 basis points average slippage
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Full P&L and performance tracking
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Zero capital at risk
                  </li>
                </ul>
              </div>
              <div className="p-5 rounded-xl bg-background/50 border border-border">
                <h4 className="font-bold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-cyan-400" />
                  Live Trading Mode
                </h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                    Same 950M+/sec decision engine
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                    Full broker/exchange integration
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                    Real order execution
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                    Real P&L and position tracking
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                    One config change to switch
                  </li>
                </ul>
              </div>
            </div>

            {/* Use cases */}
            <div className="grid md:grid-cols-4 gap-4">
              {paperTradingFeatures.map((feature, index) => (
                <div key={index} className="text-center p-4 rounded-lg bg-background/30">
                  <feature.icon className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <div className="font-medium text-white text-sm">{feature.title}</div>
                  <div className="text-xs text-muted-foreground">{feature.description}</div>
                </div>
              ))}
            </div>

            {/* Bottom message */}
            <div className="mt-8 p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
              <p className="text-green-400 font-medium">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                Full strategy validation environment included. Same 950M+ decisions/sec throughput, zero capital risk.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
